import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Form, Button, Badge, Alert } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getTelegramAlertStock, updateTelegramAlertStockItem } from '../../../services/SettingsService';

function ThresholdCell({ row, onSave }) {
    const { t } = useTranslation();
    const schema = Yup.object({
        threshold: Yup.number().typeError(t('telegramAlerts.errors.number')).integer(t('telegramAlerts.errors.number'))
            .min(0, t('telegramAlerts.errors.number')).max(100000, t('telegramAlerts.errors.number')).required(t('telegramAlerts.errors.number')),
    });
    return (
        <Formik initialValues={{ threshold: row.threshold }} validationSchema={schema} enableReinitialize
            onSubmit={(values, { setSubmitting }) => onSave(row, { threshold: Number(values.threshold) }).finally(() => setSubmitting(false))}>
            {({ values, errors, handleChange, submitForm, isValid }) => (
                <Form noValidate onSubmit={(e) => { e.preventDefault(); submitForm(); }}>
                    <Form.Control size="sm" type="number" min="0" name="threshold" value={values.threshold}
                        aria-label={t('telegramAlerts.table.myThreshold')} isInvalid={!!errors.threshold} className="stock-alerts-threshold"
                        onChange={handleChange}
                        onBlur={() => { if (isValid && Number(values.threshold) !== row.threshold) submitForm(); }} />
                </Form>
            )}
        </Formik>
    );
}

function reasonText(t, reason) {
    if (!reason) return '';
    if (reason.code === 'manual') return t('telegramAlerts.table.reason.manual');
    if (reason.code === 'default_history') {
        return t('telegramAlerts.table.reason.defaultHistory', { threshold: reason.default, days: reason.shop_days, need: reason.need });
    }
    const base = t('telegramAlerts.table.reason.auto', { avg: reason.avg, days: reason.days, raw: reason.raw });
    return reason.min_applied ? `${base} ${t('telegramAlerts.table.reason.minApplied', { min: reason.min })}` : base;
}

export default function StockAlertsTable({ shopifyConnected }) {
    const { t } = useTranslation();
    const [state, setState] = useState({ loading: true, rows: [], connected: shopifyConnected, error: false, calculatedOn: null });

    const load = useCallback(async () => {
        try {
            const res = await getTelegramAlertStock();
            setState({ loading: false, rows: res.data.rows || [], connected: res.data.connected, error: false, calculatedOn: res.data.calculated_on });
        } catch {
            setState((s) => ({ ...s, loading: false, error: true }));
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (row, body) => {
        try {
            await updateTelegramAlertStockItem(row.key, body);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.error || t('telegramAlerts.errors.save'));
        }
    };

    return (
        <Card className="mt-4">
            <Card.Header>
                <div>
                    <h4 className="heading mb-0">{t('telegramAlerts.table.title')}</h4>
                    <span className="text-muted">{t('telegramAlerts.table.subtitle')}</span>
                </div>
            </Card.Header>
            <Card.Body>
                {state.loading ? (
                    <div className="text-center py-5"><div className="spinner-border text-primary" role="status" /></div>
                ) : !state.connected ? (
                    <Alert variant="warning" className="mb-0">{t('telegramAlerts.status.shopifyHint')}</Alert>
                ) : state.error ? (
                    <Alert variant="danger" className="mb-0">{t('telegramAlerts.table.loadError')}</Alert>
                ) : (
                    <div className="table-responsive">
                        <Table hover size="sm" className="align-middle">
                            <thead>
                                <tr>
                                    <th>{t('telegramAlerts.table.product')}</th>
                                    <th className="text-end">{t('telegramAlerts.table.stock')}</th>
                                    <th className="text-end">{t('telegramAlerts.table.avgPerDay')}</th>
                                    <th className="text-end">{t('telegramAlerts.table.daysLeft')}</th>
                                    <th className="text-end">{t('telegramAlerts.table.suggested')}</th>
                                    <th>{t('telegramAlerts.table.myThreshold')}</th>
                                    <th>{t('telegramAlerts.table.mode')}</th>
                                    <th>{t('telegramAlerts.table.why')}</th>
                                    <th>{t('telegramAlerts.table.alerts')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {state.rows.map((row) => (
                                    <tr key={row.key}>
                                        <td>{row.product}</td>
                                        <td className="text-end">{row.stock}</td>
                                        <td className="text-end">{row.avg_per_day.toFixed(2)}</td>
                                        <td className="text-end">{row.days_left === null ? '-' : row.days_left.toFixed(1)}</td>
                                        <td className="text-end">{row.suggested}</td>
                                        <td><ThresholdCell row={row} onSave={save} /></td>
                                        <td className="text-nowrap">
                                            <Badge bg={row.mode === 'manual' ? 'primary' : 'secondary'} className="me-2">
                                                {t(`telegramAlerts.table.mode_${row.mode}`)}
                                            </Badge>
                                            {row.mode === 'manual' && (
                                                <Button variant="link" size="sm" className="p-0" onClick={() => save(row, { threshold: null })}>
                                                    {t('telegramAlerts.table.resetAuto')}
                                                </Button>
                                            )}
                                        </td>
                                        <td className="small text-muted stock-alerts-why">{reasonText(t, row.reason)}</td>
                                        <td>
                                            <Form.Check type="switch" id={`stock-alert-${row.key}`} checked={row.alerts_on}
                                                aria-label={t('telegramAlerts.table.alerts')} onChange={(e) => save(row, { alerts: e.target.checked })} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
                {state.calculatedOn && <p className="text-muted small mb-0 mt-2">{t('telegramAlerts.table.calculatedOn', { date: state.calculatedOn })}</p>}
            </Card.Body>
        </Card>
    );
}
